<script setup>
import { computed, ref, watch } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { Link } from '@inertiajs/vue3';
import { Head } from '@inertiajs/vue3';
import Notifications from '@/Components/Notifications.vue';

const props = defineProps({
    pageTitle: {
        type: String,
        default: null
    }
});

const page = usePage();
const notificationList = ref(null);
const toasts = computed(() => page.props.toasts);
watch(toasts, function (toasts) {
  toasts.forEach(message => {
    notificationList.value.add({title: message.title, text: message.body, type: message.severity});
  })
})

// Sidebar state
const sidebarOpen = ref(false);
const sidebarCollapsed = ref(false);
const textVisible = ref(true);

// Toggle sidebar collapsed state
const toggleSidebar = () => {
    if (!sidebarCollapsed.value) {
        // COLLAPSING: First hide text, then shrink sidebar
        textVisible.value = false;
        
        // Wait for text to fade out before collapsing sidebar width
        setTimeout(() => {
            sidebarCollapsed.value = true;
        }, 200); // Matches the fade-out duration
    } else {
        // EXPANDING: First expand sidebar, then show text
        sidebarCollapsed.value = false;
        
        // Wait for sidebar to expand before showing text
        setTimeout(() => {
            textVisible.value = true;
        }, 300);
    }
};

// Get page title from route name or page component
const pageTitle = computed(() => {
    // Use prop if provided, otherwise fall back to route-based title
    if (props.pageTitle) return props.pageTitle;
    
    const component = page.component;
    if (component === 'Welcome') return 'Dashboard';
    if (component === 'Forms/Index') return 'Formulieren overzicht';
    if (component === 'Forms/Create') return 'Nieuw formulier aanmaken';
    if (component === 'Forms/Edit') {
        // Check if we have form data with form_id
        const form = page.props.form;
        return `Formulier OPL-${form.id} aanpassen`;
    }
    if (component === 'Forms/Settings') return 'Formulieren instellingen';
    return 'SJHT Portal';
});

// Check if current route is active
const isActiveRoute = (path) => {
    return page.url === path || page.url.startsWith(path + '/');
};
</script>

<template>
    <Head :title="pageTitle" />

    <div class="flex h-screen bg-gray-50">
        <Notifications ref="notificationList" />
        
        <!-- Sidebar (always visible on desktop) -->
        <aside :class="[sidebarCollapsed ? 'md:w-16' : 'md:w-64', 'hidden md:flex md:flex-col fixed inset-y-0 z-30 bg-hbz shadow-xl transition-all duration-300']">
            <div class="flex-1 flex flex-col min-h-0">
                <div class="flex items-center h-16 px-4 bg-hb">
                    <img src="/public/img/logo.svg" alt="SJHT Logo" :class="[sidebarCollapsed ? 'mx-auto' : '', 'h-8 w-auto']">
                </div>
                <div class="flex-1 flex flex-col pt-6 pb-4 overflow-y-auto">
                    <nav class="flex-1 px-3 space-y-2">
                        <Link href="/" class="group flex items-center px-4 py-3 text-base font-medium rounded-lg transition-all duration-200" 
                              :class="[
                                  isActiveRoute('/') && page.component === 'Welcome' ? 'bg-white text-hbz shadow-sm font-semibold' : 'text-white/80 hover:bg-hb hover:text-white',
                                  sidebarCollapsed ? 'justify-center' : ''
                              ]">
                            <font-awesome-icon :icon="['fas', 'grid-horizontal']" class="h-5 w-5" 
                                             :class="[
                                                 sidebarCollapsed ? '' : 'mr-3',
                                                 isActiveRoute('/') && page.component === 'Welcome' ? 'text-hbz' : 'text-white/70 group-hover:text-white'
                                             ]" />
                            <transition name="fade">
                                <span v-if="!sidebarCollapsed && textVisible" class="whitespace-nowrap">Dashboard</span>
                            </transition>
                        </Link>
                        
                        <Link href="/formulieren" class="group flex items-center px-4 py-3 text-base font-medium rounded-lg transition-all duration-200" 
                              :class="[
                                  isActiveRoute('/formulieren') ? 'bg-white text-hbz shadow-sm font-semibold' : 'text-white/80 hover:bg-hb hover:text-white',
                                  sidebarCollapsed ? 'justify-center' : ''
                              ]">
                            <font-awesome-icon :icon="['fas', 'paper-plane']" class="h-5 w-5" 
                                             :class="[
                                                 sidebarCollapsed ? '' : 'mr-3',
                                                 isActiveRoute('/formulieren') ? 'text-hbz' : 'text-white/70 group-hover:text-white'
                                             ]" />
                            <transition name="fade">
                                <span v-if="!sidebarCollapsed && textVisible" class="whitespace-nowrap">Formulieren</span>
                            </transition>
                        </Link>
                        
                        <!-- Future navigation items can be added here -->
                    </nav>
                </div>
                <div class="flex-shrink-0 flex flex-col bg-hb">
                    <button 
                        @click="toggleSidebar" 
                        class="mb-2 mx-3 mt-2 p-2 rounded-md text-white/70 hover:bg-hbz hover:text-white flex items-center transition-all duration-200"
                        :class="sidebarCollapsed ? 'justify-center' : 'justify-end'"
                    >
                        <font-awesome-icon 
                            :icon="['fas', sidebarCollapsed ? 'chevron-right' : 'chevron-left']" 
                            class="h-4 w-4" 
                        />
                        <font-awesome-icon 
                            v-if="!sidebarCollapsed"
                            :icon="['fas', sidebarCollapsed ? 'chevron-right' : 'chevron-left']" 
                            class="h-4 w-4 -ml-1" 
                        />
                    </button>
                    <div :class="[sidebarCollapsed ? 'justify-center' : '', 'flex items-center']" class="border-t border-hbz/50 p-4">
                        <div class="flex-shrink-0">
                            <div class="h-8 w-8 rounded-full bg-hbz/30 flex items-center justify-center text-white">
                                <font-awesome-icon :icon="['fas', 'user']" class="h-4 w-4" />
                            </div>
                        </div>
                        <transition name="fade">
                            <div v-if="!sidebarCollapsed && textVisible" class="ml-3">
                                <p class="text-sm font-medium text-white">Admin Gebruiker</p>
                                <a href="/logout" class="text-xs font-medium text-white/70 hover:text-white transition-colors">Uitloggen</a>
                            </div>
                        </transition>
                    </div>
                </div>
            </div>
        </aside>

        <!-- Mobile hamburger menu with transition -->
        <div class="md:hidden">
            <button @click="sidebarOpen = !sidebarOpen" class="fixed z-30 top-3 left-3 bg-hbz p-2 rounded-md text-white transition-all duration-200 hover:bg-hb">
                <font-awesome-icon :icon="['fas', 'bars']" class="h-6 w-6" />
            </button>
            
            <!-- Mobile sidebar (overlay) with transition -->
            <div class="fixed inset-0 z-40 flex" :class="sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'" style="transition: opacity 0.3s ease-in-out;">
                <div class="fixed inset-0 bg-black/50" @click="sidebarOpen = false" style="transition: opacity 0.3s ease-in-out;"></div>
                <div class="relative flex-1 flex flex-col max-w-xs w-full bg-hbz transform transition-transform duration-300 ease-in-out" 
                     :class="sidebarOpen ? 'translate-x-0' : '-translate-x-full'">
                    <!-- Close button -->
                    <div class="absolute top-3 right-3">
                        <button @click="sidebarOpen = false" class="rounded-md p-2 text-hbz bg-white hover:bg-gray-100 hover:text-hb transition-colors">
                            <font-awesome-icon :icon="['fas', 'times']" class="h-6 w-6" />
                        </button>
                    </div>
                    <div class="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                        <div class="flex-shrink-0 flex items-center px-4">
                            <img src="/public/img/logo.svg" alt="SJHT Logo" class="h-8 w-auto">
                        </div>
                        <nav class="mt-5 px-2 space-y-1">
                            <Link href="/" class="group flex items-center px-3 py-2 text-sm font-medium rounded-md" @click="sidebarOpen = false" 
                                  :class="isActiveRoute('/') && page.component === 'Welcome' ? 'bg-white text-gray-800 font-semibold' : 'text-white/80 hover:bg-hb hover:text-white'">
                                <font-awesome-icon :icon="['fas', 'grid-horizontal']" class="h-5 w-5 mr-2" 
                                                 :class="isActiveRoute('/') && page.component === 'Welcome' ? 'text-hbz' : 'text-white/70 group-hover:text-white'" />
                                <span>Dashboard</span>
                            </Link>
                            
                            <Link href="/formulieren" class="group flex items-center px-3 py-2 text-sm font-medium rounded-md" @click="sidebarOpen = false" 
                                  :class="isActiveRoute('/formulieren') ? 'bg-white text-hbz font-semibold' : 'text-white/80 hover:bg-hb hover:text-white'">
                                <font-awesome-icon :icon="['fas', 'paper-plane']" class="h-5 w-5 mr-2" 
                                                 :class="isActiveRoute('/formulieren') ? 'text-hbz' : 'text-white/70 group-hover:text-white'" />
                                <span>Formulieren</span>
                            </Link>
                        </nav>
                    </div>
                </div>
            </div>
        </div>

        <!-- Main content -->
        <div :class="[sidebarCollapsed ? 'md:pl-16' : 'md:pl-64', 'flex flex-col flex-1 w-full transition-all duration-300']">
            <!-- Top header - made sticky -->
            <header class="bg-white shadow-sm h-16 z-20 sticky top-0">
                <div class="px-4 pl-16 md:pl-4 h-full flex items-center justify-between">
                    <h1 class="text-xl md:text-2xl font-bold text-hbz">{{ pageTitle }}</h1>
                    <!-- <div class="flex items-center">
                        <span class="text-sm text-hbz mr-2">Gebruiker</span>
                        <div class="h-8 w-8 rounded-full bg-hbz/10 flex items-center justify-center text-hbz">
                            <font-awesome-icon :icon="['fas', 'user']" class="h-4 w-4" />
                        </div>
                    </div> -->
                </div>
            </header>

            <!-- Page content -->
            <main class="flex-1 overflow-y-auto relative">
                <slot />
            </main>
        </div>
    </div>
</template>

<style scoped>
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.2s ease;
}
.fade-enter-from, .fade-leave-to {
    opacity: 0;
}
</style>